import { DeleteIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useDeleteWorkflowDefinition } from '@/hooks'
import type { WorkflowDefinitionSummary } from '@/types'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface DeleteWorkflowDefinitionDialogProps {
  projectId: string
  definition: WorkflowDefinitionSummary | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteWorkflowDefinitionDialog({ projectId, definition, open, onOpenChange }: DeleteWorkflowDefinitionDialogProps) {
  const [confirmationText, setConfirmationText] = useState('')
  const deleteDefinition = useDeleteWorkflowDefinition(projectId)

  const isConfirmationValid = definition ? confirmationText === definition.displayName : false

  useEffect(() => {
    if (!open) {
      setConfirmationText('')
    }
  }, [open])

  const handleDelete = async () => {
    if (!isConfirmationValid || !definition) return

    try {
      await deleteDefinition.mutateAsync(definition.id)
      toast.success('Workflow definition deleted successfully')
      onOpenChange(false)
    } catch (error) {
      const apiError = error as {
        response?: {
          data?: Array<{ field?: string; errorMessage?: string }>
        }
      }

      const validationErrors = apiError?.response?.data
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        toast.error(validationErrors[0].errorMessage || 'Failed to delete workflow definition')
      } else {
        toast.error('Failed to delete workflow definition.')
      }
    }
  }

  if (!definition) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Delete Workflow Definition</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          Are you sure you want to delete the definition "<span className="font-bold">{definition.displayName}</span>"?
        </DialogDescription>
        <div className="space-y-2">
          <Input
            placeholder="Enter workflow definition display name"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            className="w-full"
            aria-label="Type workflow definition name to confirm deletion"
          />
          <p className="text-sm text-muted-foreground">
            Type "{definition.displayName}" to enable the Delete Workflow Definition button.
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmationValid || deleteDefinition.isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DeleteIcon className="h-6 w-6" />
            {deleteDefinition.isPending ? 'Deleting...' : 'Delete Workflow Definition'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
